import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { coolingAPI } from "../coolingAPI";
import { isValidDate } from "../../../utils";

const MAX_CACHE_TIME = 60 * 60 * 1000; // 1ч
export const COOLING_INFO_QUERY_KEY = "cooling-info";

export const useGetCoolingInfoQuery = () => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [COOLING_INFO_QUERY_KEY],
    queryFn: async () => {
      const res = await coolingAPI.getCoolingInfo();

      if (!res) {
        throw new Error("Empty cooling response");
      }

      if (typeof res.isLimitCooldown !== "boolean") {
        throw new Error(`isLimitCooldown type must be boolean. Current type is: ${typeof res.isLimitCooldown}`);
      }

      if (res.isLimitCooldown && !isValidDate(res.limitInfo?.cooldownEndDateTime)) {
        throw new Error(`Invalid date 'cooldownEndDateTime': ${res.limitInfo?.cooldownEndDateTime}`);
      }

      return res;
    },

    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    const data = query.data;
    if (!data) return;

    if (!data.isLimitCooldown) {
      queryClient.setQueryDefaults([COOLING_INFO_QUERY_KEY], {
        staleTime: 60 * 1000, // 1мин
        gcTime: 5 * 60 * 1000, // 5мин
      });
      return;
    }

    const finishAt = new Date(data.limitInfo!.cooldownEndDateTime!).getTime();
    const untilFinish = finishAt - Date.now();

    // For case when user device has incorrect time OR server sent paste time with cooling status true
    if (untilFinish < 0) {
      queryClient.setQueryDefaults([COOLING_INFO_QUERY_KEY], {
        staleTime: 60 * 1000, // 1мин
        gcTime: 60 * 1000, // 1мин
      });
      return;
    }

    queryClient.setQueryDefaults([COOLING_INFO_QUERY_KEY], {
      staleTime: Math.min(untilFinish, MAX_CACHE_TIME),
      gcTime: Math.min(untilFinish, MAX_CACHE_TIME),
    });

    const id = setTimeout(() => {
      void queryClient.invalidateQueries({ queryKey: [COOLING_INFO_QUERY_KEY] });
    }, untilFinish);

    return () => {
      clearTimeout(id);
    };
  }, [query.data, queryClient]);

  return query;
};
